import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Icons } from '../Icons';
import { useLanguage } from '../../contexts/LanguageContext';
import { APIService, secureFetch as fetch } from '../../services/api';
import { publicUrl } from '../../config/publicOrigin';
const AgencyFullAnalysis = lazy(() => import('./AgencyFullAnalysis'));
const EshopHealthCheckPage = lazy(() => import('../../pages/EshopHealthCheckPage'));
const HealthCheckPage = lazy(() => import('../../pages/HealthCheckPage'));
const ScriptMonitoringDashboardTab = lazy(() => import('../agency/ScriptMonitoringDashboardTab'));
const SitemapMonitoringDashboardTab = lazy(() => import('../agency/SitemapMonitoringDashboardTab'));

const getScoreGrade = (score) => {
  if (score >= 90) return { grade: 'A', color: 'green', bg: 'bg-green-500', text: 'text-green-500', light: 'bg-green-500/10' };
  if (score >= 75) return { grade: 'B', color: 'lime', bg: 'bg-lime-500', text: 'text-lime-500', light: 'bg-lime-500/10' };
  if (score >= 50) return { grade: 'C', color: 'yellow', bg: 'bg-yellow-500', text: 'text-yellow-500', light: 'bg-yellow-500/10' };
  if (score >= 25) return { grade: 'D', color: 'orange', bg: 'bg-orange-500', text: 'text-orange-500', light: 'bg-orange-500/10' };
  return { grade: 'F', color: 'red', bg: 'bg-red-500', text: 'text-red-500', light: 'bg-red-500/10' };
};

const ClientCard = ({ client, onDelete, onUpdate, onGenerateToken, onSendReport, displayDomain, t, language, isExpanded, onToggle }) => {
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const score = client.latest_score != null ? parseInt(client.latest_score) : null;
  const gradeInfo = score != null ? getScoreGrade(score) : { grade: '-', color: 'slate', bg: 'bg-slate-500', text: 'text-slate-500', light: 'bg-slate-500/10' };
  
  const handleGenerateToken = async () => {
    setGenerating(true);
    try {
      await onGenerateToken(client.id);
    } finally {
      setGenerating(false);
    }
  };
  
  const handleSendReport = async () => {
    setSending(true);
    try {
      await onSendReport(client.id);
    } finally {
      setSending(false);
    }
  };
  
  const copyLink = () => {
    if (!client.access_token) return;
    const url = publicUrl(`/client-dashboard/${client.access_token}`);
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className={`rounded-xl border transition-all duration-300 ${isExpanded ? 'bg-white dark:bg-slate-800 border-purple-500/50 shadow-lg shadow-purple-500/10' : 'bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'}`}>
      <div 
        className="flex items-center justify-between p-4 cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-center gap-4">
          <div className={`w-3 h-3 rounded-full ${gradeInfo.bg}`} />
          <div className="min-w-0">
            <div className="font-semibold text-slate-900 dark:text-white">{client.client_name}</div>
            {client.origin && (
              <div className="text-sm text-slate-500 dark:text-slate-400 truncate">
                {displayDomain(client.origin)}
              </div>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {client.access_token && (
            <span className="hidden sm:inline-flex px-2 py-1 rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-xs font-medium text-emerald-700 dark:text-emerald-400">
              {t('agency.dashboardActive')}
            </span>
          )}
          {client.report_frequency && client.report_frequency !== 'none' && (
            <span className="hidden sm:inline-flex px-2 py-1 rounded-full bg-purple-100 dark:bg-purple-500/20 text-xs font-medium text-purple-700 dark:text-purple-400">
              {client.report_frequency === 'weekly' ? t('agency.weeklyReport') : t('agency.monthlyReport')}
            </span>
          )}
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg ${gradeInfo.light} ${gradeInfo.text}`}>
            {gradeInfo.grade}
          </div>
          <Icons.ChevronDown className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
        </div>
      </div>
      
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-slate-100 dark:border-slate-700/50 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/50">
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">{t('agency.businessCategory')}</label>
              <select
                value={client.business_category || 'ecommerce'}
                onChange={(e) => onUpdate(client.id, { businessCategory: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
              >
                <option value="ecommerce">{t('agency.categories.ecommerce')}</option>
                <option value="publisher">{t('agency.categories.publisher')}</option>
                <option value="saas">{t('agency.categories.saas')}</option>
                <option value="leadgen">{t('agency.categories.leadgen')}</option>
                <option value="corporate">{t('agency.categories.corporate')}</option>
              </select>
            </div>
            
            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/50">
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">{t('agency.reportFrequency')}</label>
              <select
                value={client.report_frequency || 'none'}
                onChange={(e) => onUpdate(client.id, { reportFrequency: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
              >
                <option value="none">{t('agency.frequencies.none')}</option>
                <option value="weekly">{t('agency.frequencies.weekly')}</option>
                <option value="monthly">{t('agency.frequencies.monthly')}</option>
              </select>
            </div>
            
            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/50 flex items-center">
              <label className="flex items-center gap-3 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={client.notify_on_degradation || false}
                    onChange={(e) => onUpdate(client.id, { notifyOnDegradation: e.target.checked })}
                    className="sr-only"
                  />
                  <div className={`w-12 h-6 rounded-full transition-colors ${client.notify_on_degradation ? 'bg-purple-500' : 'bg-slate-300 dark:bg-slate-600'}`}>
                    <div className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform ${client.notify_on_degradation ? 'translate-x-6' : 'translate-x-0.5'} mt-0.5`} />
                  </div>
                </div>
                <span className="text-sm text-slate-700 dark:text-slate-300">{t('agency.notifyOnDegradation')}</span>
              </label>
            </div>
          </div>
          
          <div className="p-4 rounded-xl bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-500/10 dark:to-blue-500/10 border border-cyan-200 dark:border-cyan-500/30">
            <label className="block text-xs font-medium text-cyan-700 dark:text-cyan-400 mb-3">{t('agency.clientDashboard')}</label>
            {client.access_token ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 p-3 rounded-lg bg-white dark:bg-slate-800/80">
                  <Icons.Link className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <input
                    type="text"
                    readOnly
                    value={publicUrl(`/client-dashboard/${client.access_token}`)}
                    className="flex-1 min-w-0 bg-transparent text-sm text-slate-600 dark:text-slate-400 focus:outline-none truncate"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={copyLink}
                    className="flex-1 sm:flex-none px-4 py-2.5 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-white text-sm font-medium transition-colors flex items-center justify-center gap-2 border border-slate-200 dark:border-slate-600"
                  >
                    {copied ? <Icons.Check className="w-4 h-4 text-green-500" /> : <Icons.Copy className="w-4 h-4" />}
                    {copied ? t('agency.copied') : t('agency.copy')}
                  </button>
                  <button
                    onClick={handleGenerateToken}
                    disabled={generating}
                    className="px-4 py-2.5 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-white text-sm font-medium transition-colors flex items-center gap-2 border border-slate-200 dark:border-slate-600 disabled:opacity-50"
                  >
                    <Icons.Refresh className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
                    {t('agency.regenerateLink')}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={handleGenerateToken}
                disabled={generating}
                className="w-full px-4 py-3 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white text-sm font-medium disabled:opacity-50 transition-all flex items-center justify-center gap-2 shadow-lg shadow-cyan-500/25"
              >
                {generating ? (
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                ) : (
                  <Icons.Link className="w-4 h-4" />
                )}
                {t('agency.generateLink')}
              </button>
            )}
          </div>
          
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <div className="flex gap-2">
              {client.client_email && client.origin_id && (
                <button
                  onClick={handleSendReport}
                  disabled={sending}
                  className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white text-sm font-medium disabled:opacity-50 transition-all flex items-center gap-2 shadow-lg shadow-purple-500/25"
                >
                  {sending ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  ) : (
                    <Icons.Mail className="w-4 h-4" />
                  )}
                  {t('agency.sendReportNow')}
                </button>
              )}
            </div>
            <button
              onClick={() => onDelete(client.id)}
              className="px-4 py-2.5 rounded-lg bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 text-red-600 dark:text-red-400 text-sm font-medium transition-colors flex items-center gap-2"
            >
              <Icons.Trash className="w-4 h-4" />
              {t('agency.deleteClient')}
            </button>
          </div>
          
          {client.last_report_sent_at && (
            <p className="text-xs text-slate-500 dark:text-slate-400 pt-2 border-t border-slate-100 dark:border-slate-700/50">
              {t('agency.lastReportSent')}: {new Date(client.last_report_sent_at).toLocaleString(language === 'cs' ? 'cs-CZ' : 'en-US')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, value, label, color = 'cyan' }) => {
  const colors = {
    cyan: 'from-cyan-500 to-blue-600 shadow-cyan-500/25',
    purple: 'from-purple-500 to-pink-600 shadow-purple-500/25',
    green: 'from-emerald-500 to-teal-600 shadow-emerald-500/25',
    orange: 'from-orange-500 to-red-600 shadow-orange-500/25'
  };
  
  return (
    <div className="p-4 rounded-xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colors[color]} shadow-lg flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">{value}</div>
          <div className="text-sm text-slate-500 dark:text-slate-400">{label}</div>
        </div>
      </div>
    </div>
  );
};

const ToolResultDisplay = ({ result, t }) => {
  const { module, data } = result;
  
  if (module === 'security' && data.security) {
    const sec = data.security;
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4 mb-4">
          <div className={`text-4xl font-bold ${sec.score >= 70 ? 'text-green-500' : sec.score >= 40 ? 'text-yellow-500' : 'text-red-500'}`}>
            {sec.score}/100
          </div>
          <div className="text-sm text-slate-500 dark:text-slate-400">{t('quickTools.securityScore')}</div>
        </div>
        <div className="grid gap-3">
          {sec.headers && Object.entries(sec.headers).map(([header, info]) => (
            <div key={header} className={`p-4 rounded-xl ${info.present ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm text-slate-900 dark:text-white">{header}</span>
                <span className={`text-xs px-2 py-1 rounded-full ${info.present ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                  {info.present ? t('common.present') : t('common.missing')}
                </span>
              </div>
              {info.value && <div className="mt-2 text-xs text-slate-500 font-mono truncate">{info.value}</div>}
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  if (module === 'email' && data) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4 mb-4">
          <div className={`text-4xl font-bold ${data.score >= 70 ? 'text-green-500' : data.score >= 40 ? 'text-yellow-500' : 'text-red-500'}`}>
            {data.score || 0}/100
          </div>
          <div className="text-sm text-slate-500 dark:text-slate-400">{t('quickTools.emailScore')}</div>
        </div>
        <div className="grid gap-3">
          {['spf', 'dkim', 'dmarc', 'mx'].map(type => (
            <div key={type} className={`p-4 rounded-xl ${data[type]?.valid ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
              <div className="flex items-center justify-between">
                <span className="font-bold text-slate-900 dark:text-white uppercase">{type}</span>
                <span className={`text-xs px-2 py-1 rounded-full ${data[type]?.valid ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                  {data[type]?.valid ? t('common.valid') : t('common.invalid')}
                </span>
              </div>
              {data[type]?.record && <div className="mt-2 text-xs text-slate-500 font-mono break-all">{data[type].record}</div>}
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  if (module === 'techstack' && data.techstack) {
    const tech = data.techstack;
    return (
      <div className="space-y-4">
        {tech.technologies?.length > 0 ? (
          <div className="grid gap-3">
            {tech.technologies.map((item, i) => (
              <div key={i} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Icons.Cpu className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900 dark:text-white">{item.name}</div>
                    {item.categories && <div className="text-xs text-slate-500">{item.categories.join(', ')}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-500 text-center py-8">{t('quickTools.noTechDetected')}</p>
        )}
      </div>
    );
  }
  
  if (module === 'schema' && data.schema) {
    const schema = data.schema;
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4 mb-4">
          <div className={`text-4xl font-bold ${schema.score >= 70 ? 'text-green-500' : schema.score >= 40 ? 'text-yellow-500' : 'text-red-500'}`}>
            {schema.score || 0}/100
          </div>
          <div className="text-sm text-slate-500 dark:text-slate-400">{t('quickTools.schemaScore')}</div>
        </div>
        {schema.schemas?.length > 0 && (
          <div className="space-y-2">
            {schema.schemas.map((s, i) => (
              <div key={i} className="p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-sm text-green-400">
                {s['@type'] || s.type || JSON.stringify(s).substring(0, 100)}
              </div>
            ))}
          </div>
        )}
        {schema.issues?.length > 0 && (
          <div className="space-y-2">
            {schema.issues.map((issue, i) => (
              <div key={i} className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-sm text-yellow-400">
                {issue}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (module === 'carbon' && data.carbon) {
    const carbon = data.carbon;
    return (
      <div className="space-y-4">
        <div className="text-center py-4">
          <div className="text-5xl font-bold text-emerald-400 mb-2">{carbon.grams?.toFixed(2) || '0'}g</div>
          <div className="text-slate-500">{t('quickTools.co2PerPageview')}</div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/50 text-center">
            <div className="text-2xl font-bold text-slate-900 dark:text-white">{carbon.cleanerThan || 0}%</div>
            <div className="text-xs text-slate-500">{t('quickTools.cleanerThan')}</div>
          </div>
          <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/50 text-center">
            <div className={`text-2xl font-bold ${carbon.green ? 'text-green-400' : 'text-slate-400'}`}>
              {carbon.green ? t('common.yes') : t('common.no')}
            </div>
            <div className="text-xs text-slate-500">{t('quickTools.greenHosting')}</div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/50">
      <pre className="text-xs text-slate-600 dark:text-slate-400 overflow-auto max-h-96">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
};

const AgencyPanel = ({ origins = [], onClose, initialTab = null }) => {
  const { t, language } = useLanguage();
  const [status, setStatus] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedClientId, setExpandedClientId] = useState(null);
  const [newClient, setNewClient] = useState({ clientName: '', clientEmail: '', originId: '' });
  const [settings, setSettings] = useState({ 
    agencyName: '', 
    agencyLogoUrl: '',
    primaryColor: '#8B5CF6',
    secondaryColor: '#EC4899',
    contactEmail: '',
    contactPhone: '',
    website: '',
    reportFooter: '',
    defaultReportLanguage: 'en'
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab || 'clients');
  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);
  
  const [alerts, setAlerts] = useState([]);
  const [alertsFeatures, setAlertsFeatures] = useState({ has_access: false });
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [showAddAlert, setShowAddAlert] = useState(false);
  const [editingAlertId, setEditingAlertId] = useState(null);
  const [newAlert, setNewAlert] = useState({ 
    domain: '', 
    frequency: 'daily',
    alert_on_lcp_change: 20, 
    alert_on_inp_change: 20,
    alert_on_cls_change: 50,
    slackWebhook: '', 
    discordWebhook: '' 
  });
  const [userTier, setUserTier] = useState(null);
  
  const [domainGroups, setDomainGroups] = useState([]);
  const [allDomains, setAllDomains] = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', description: '' });
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [expandedMenus, setExpandedMenus] = useState({});
  
  const [toolUrl, setToolUrl] = useState('');
  const [toolDomain, setToolDomain] = useState('');
  const [toolLoading, setToolLoading] = useState(false);
  const [toolResult, setToolResult] = useState(null);
  const [toolError, setToolError] = useState(null);
  
  const [cruxUrl, setCruxUrl] = useState('');
  const [cruxFormFactor, setCruxFormFactor] = useState('PHONE');
  const [cruxLoading, setCruxLoading] = useState(false);
  const [cruxResult, setCruxResult] = useState(null);
  const [cruxError, setCruxError] = useState(null);
  
  const [lighthouseUrl, setLighthouseUrl] = useState('');
  const [lighthouseFormFactor, setLighthouseFormFactor] = useState('mobile');
  const [lighthouseLoading, setLighthouseLoading] = useState(false);
  const [lighthouseResult, setLighthouseResult] = useState(null);
  const [lighthouseError, setLighthouseError] = useState(null);

  const toggleMenu = (menu) => {
    setExpandedMenus(prev => ({ ...prev, [menu]: !prev[menu] }));
  };

  const toolModuleMap = {
    'security-headers': 'security',
    'email-deliverability': 'email',
    'og-social': 'ogpreview',
    'html-validator': 'html',
    'schema-seo': 'schema',
    'tech-stack': 'techstack',
    'dns-ssl': 'dns',
    'carbon': 'carbon',
    'pwa': 'pwa',
    'robots-sitemap': 'robots',
    'headings': 'headings',
    'rich-results': 'richresults'
  };

  const quickTools = [
    { id: 'security-headers', icon: Icons.Shield, labelKey: 'quickTools.securityHeaders' },
    { id: 'email-deliverability', icon: Icons.Mail, labelKey: 'quickTools.emailDeliverability' },
    { id: 'og-social', icon: Icons.Share, labelKey: 'quickTools.ogPreview' },
    { id: 'html-validator', icon: Icons.Code, labelKey: 'quickTools.htmlValidator' },
    { id: 'schema-seo', icon: Icons.Search, labelKey: 'quickTools.schemaSeo' },
    { id: 'tech-stack', icon: Icons.Cpu, labelKey: 'quickTools.techStack' },
    { id: 'dns-ssl', icon: Icons.Lock, labelKey: 'quickTools.dnsSsl' },
    { id: 'carbon', icon: Icons.Leaf, labelKey: 'quickTools.carbonFootprint' },
    { id: 'pwa', icon: Icons.Smartphone, labelKey: 'quickTools.pwaAudit' },
    { id: 'robots-sitemap', icon: Icons.FileText, labelKey: 'quickTools.robotsSitemap' },
    { id: 'headings', icon: Icons.Heading, labelKey: 'quickTools.headingsAudit' },
    { id: 'rich-results', icon: Icons.Star, labelKey: 'quickTools.richResults' },
    { id: 'http-protocol', icon: Icons.Globe, labelKey: 'quickTools.httpProtocol', premium: true },
    { id: 'competitor', icon: Icons.BarChart, labelKey: 'quickTools.competitorBenchmark', premium: true },
    { id: 'animation-fps', icon: Icons.Play, labelKey: 'quickTools.animationFps', premium: true },
    { id: 'ab-test', icon: Icons.Beaker, labelKey: 'quickTools.abTest', premium: true }
  ];

  const cruxMetrics = [
    { id: 'lcp', label: 'LCP', labelKey: 'metrics.lcp' },
    { id: 'inp', label: 'INP', labelKey: 'metrics.inp' },
    { id: 'cls', label: 'CLS', labelKey: 'metrics.cls' },
    { id: 'fcp', label: 'FCP', labelKey: 'metrics.fcp' },
    { id: 'ttfb', label: 'TTFB', labelKey: 'metrics.ttfb' }
  ];

  const pagespeedAudits = [
    { id: 'performance', labelKey: 'lab.healthScoreComponents.performance' },
    { id: 'accessibility', labelKey: 'lab.healthScoreComponents.accessibility' },
    { id: 'best-practices', labelKey: 'lab.healthScoreComponents.bestPractices' },
    { id: 'seo', labelKey: 'lab.healthScoreComponents.seo' }
  ];

  useEffect(() => {
    loadAgencyData();
  }, []);

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const loadAgencyData = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      
      const statusRes = await fetch('/api/agency/status', { credentials: 'include' });
      if (!statusRes.ok) throw new Error((await statusRes.json().catch(() => ({}))).error || `HTTP ${statusRes.status}`);
      const statusData = await statusRes.json();
      setStatus(statusData);
      
      if (statusData.isAgency) {
        const clientsRes = await fetch('/api/agency/clients', { credentials: 'include' });
        if (!clientsRes.ok) throw new Error((await clientsRes.json().catch(() => ({}))).error || `HTTP ${clientsRes.status}`);
        const clientsData = await clientsRes.json();
        setClients(clientsData.clients || []);
        setSettings({
          agencyName: statusData.agencyName || '',
          agencyLogoUrl: statusData.agencyLogo || ''
        });
      }
    } catch (err) {
      console.error('Failed to load agency data:', err);
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAlerts = async () => {
    try {
      setAlertsLoading(true);
      const res = await fetch('/api/alerts/configurations', { credentials: 'include' });
      if (res.status === 403) {
        setAlertsFeatures({ has_access: false });
        setAlerts([]);
        return;
      }
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.configurations || []);
        setAlertsFeatures(data.features || { has_access: true });
      }
    } catch (err) {
      console.error('Failed to load alerts:', err);
      setAlertsFeatures({ has_access: false });
    } finally {
      setAlertsLoading(false);
    }
  };

  const loadDomainGroups = async () => {
    try {
      setDomainsLoading(true);
      const res = await fetch('/api/user/domains', { credentials: 'include' });
      if (res.status === 403) {
        setDomainGroups([]);
        setAllDomains([]);
        setUserTier('free');
        return;
      }
      if (res.ok) {
        const data = await res.json();
        setDomainGroups(data.groups || []);
        setAllDomains(data.domains || []);
        setUserTier(data.limits?.canCreateGroups ? 'paid' : 'free');
      }
    } catch (err) {
      console.error('Failed to load domain groups:', err);
    } finally {
      setDomainsLoading(false);
    }
  };

  const normalizeUrl = (input) => {
    let normalized = input.trim();
    if (!normalized) return '';
    if (!/^https?:\/\//i.test(normalized)) {
      normalized = 'https://' + normalized;
    }
    return normalized;
  };

  const runToolAnalysis = async (toolId) => {
    const module = toolModuleMap[toolId];
    if (!module) return;

    if (toolId === 'email-deliverability') {
      if (!toolDomain.trim()) return;
      const cleanDomain = toolDomain.trim().replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0].toLowerCase();
      
      setToolLoading(true);
      setToolError(null);
      setToolResult(null);
      
      try {
        const csrfRes = await fetch('/api/csrf-token');
        const { token: csrfToken } = await csrfRes.json();
        
        const res = await fetch('/api/email-deliverability/check', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
          credentials: 'include',
          body: JSON.stringify({ domain: cleanDomain })
        });
        
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.error || `Error: ${res.status}`);
        }
        
        const data = await res.json();
        setToolResult({ module, data, url: cleanDomain });
      } catch (err) {
        setToolError(err.message);
      } finally {
        setToolLoading(false);
      }
    } else {
      if (!toolUrl.trim()) return;
      const normalized = normalizeUrl(toolUrl);
      
      setToolLoading(true);
      setToolError(null);
      setToolResult(null);
      
      try {
        const csrfRes = await fetch('/api/csrf-token');
        const { token: csrfToken } = await csrfRes.json();
        
        const res = await fetch('/api/quick-tools', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
          credentials: 'include',
          body: JSON.stringify({ url: normalized, modules: [module] })
        });
        
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.error || `Error: ${res.status}`);
        }
        
        const data = await res.json();
        setToolResult({ module, data, url: normalized });
      } catch (err) {
        setToolError(err.message);
      } finally {
        setToolLoading(false);
      }
    }
  };

  const clearToolResult = () => {
    setToolResult(null);
    setToolError(null);
  };

  const runCruxAnalysis = async (metricId) => {
    if (!cruxUrl.trim()) return;
    const normalized = normalizeUrl(cruxUrl);
    
    setCruxLoading(true);
    setCruxError(null);
    setCruxResult(null);
    
    try {
      const formFactor = cruxFormFactor === 'ALL' ? null : cruxFormFactor;
      const data = await APIService.getCrUXData(normalized, formFactor, 'origin');
      setCruxResult({ metricId, data, url: normalized, formFactor: cruxFormFactor });
    } catch (err) {
      setCruxError(err.message || 'Chyba při načítání CrUX dat');
    } finally {
      setCruxLoading(false);
    }
  };

  const clearCruxResult = () => {
    setCruxResult(null);
    setCruxError(null);
  };

  const runLighthouseAudit = async (auditType) => {
    if (!lighthouseUrl.trim()) return;
    const normalized = normalizeUrl(lighthouseUrl);
    
    setLighthouseLoading(true);
    setLighthouseError(null);
    setLighthouseResult(null);
    
    try {
      const strategy = lighthouseFormFactor === 'DESKTOP' ? 'desktop' : 'mobile';
      const categories = ['performance', 'accessibility', 'best-practices', 'seo'];
      
      const data = await APIService.analyzeURL(normalized, strategy, categories);
      setCruxResult(null);
      setLighthouseResult({ auditType, data, url: normalized, formFactor: lighthouseFormFactor });
    } catch (err) {
      setLighthouseError(err.message || 'Chyba při spuštění auditu');
    } finally {
      setLighthouseLoading(false);
    }
  };

  const clearLighthouseResult = () => {
    setLighthouseResult(null);
    setLighthouseError(null);
  };

  const addAlert = async () => {
    try {
      setSaving(true);
      const res = await fetch('/api/alerts/configurations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          domain: newAlert.domain || null,
          frequency: newAlert.frequency,
          alert_on_lcp_change: newAlert.alert_on_lcp_change,
          alert_on_inp_change: newAlert.alert_on_inp_change,
          alert_on_cls_change: newAlert.alert_on_cls_change,
          slack_webhook_url: newAlert.slackWebhook || '',
          discord_webhook_url: newAlert.discordWebhook || '',
          is_enabled: true,
          email_enabled: true
        })
      });
      if (res.ok) {
        await loadAlerts();
        setNewAlert({ 
          domain: '', 
          frequency: 'daily',
          alert_on_lcp_change: 20, 
          alert_on_inp_change: 20,
          alert_on_cls_change: 50,
          slackWebhook: '', 
          discordWebhook: '' 
        });
        setShowAddAlert(false);
        showMessage(t('agency.alertAdded'));
      } else {
        const error = await res.json();
        showMessage(error.error || t('agency.updateError'), 'error');
      }
    } catch (err) {
      showMessage(t('agency.updateError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteAlert = async (id) => {
    if (!confirm(t('common.confirmDelete'))) return;
    try {
      await fetch(`/api/alerts/configurations/${id}`, { method: 'DELETE', credentials: 'include' });
      await loadAlerts();
      showMessage(t('agency.alertDeleted'));
    } catch (err) {
      showMessage(t('agency.updateError'), 'error');
    }
  };

  const startEditAlert = (alert) => {
    setEditingAlertId(alert.id);
    setNewAlert({
      domain: alert.domain || '',
      frequency: alert.frequency || 'daily',
      alert_on_lcp_change: alert.alert_on_lcp_change || 20,
      alert_on_inp_change: alert.alert_on_inp_change || 20,
      alert_on_cls_change: alert.alert_on_cls_change || 50,
      slackWebhook: alert.slack_webhook || '',
      discordWebhook: alert.discord_webhook || ''
    });
  };

  const cancelEditAlert = () => {
    setEditingAlertId(null);
    setNewAlert({
      domain: '',
      frequency: 'daily',
      alert_on_lcp_change: 20,
      alert_on_inp_change: 20,
      alert_on_cls_change: 50,
      slackWebhook: '',
      discordWebhook: ''
    });
  };

  const updateAlert = async (id) => {
    setSaving(true);
    try {
      const csrfRes = await fetch('/api/csrf-token');
      const { token: csrfToken } = await csrfRes.json();
      
      const res = await fetch(`/api/alerts/configurations/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        credentials: 'include',
        body: JSON.stringify({
          domain: newAlert.domain || null,
          frequency: newAlert.frequency,
          alert_on_lcp_change: parseInt(newAlert.alert_on_lcp_change),
          alert_on_inp_change: parseInt(newAlert.alert_on_inp_change),
          alert_on_cls_change: parseInt(newAlert.alert_on_cls_change),
          slack_webhook: newAlert.slackWebhook || null,
          discord_webhook: newAlert.discordWebhook || null
        })
      });
      
      if (res.ok) {
        await loadAlerts();
        cancelEditAlert();
        showMessage(t('agency.alertUpdated'));
      } else {
        const error = await res.json();
        showMessage(error.error || t('agency.updateError'), 'error');
      }
    } catch (err) {
      showMessage(t('agency.updateError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleAlert = async (id, alert) => {
    try {
      await fetch(`/api/alerts/configurations/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          domain: alert.domain,
          is_enabled: !alert.is_enabled,
          frequency: alert.frequency,
          alert_on_lcp_change: alert.alert_on_lcp_change,
          alert_on_inp_change: alert.alert_on_inp_change,
          alert_on_cls_change: alert.alert_on_cls_change,
          email_enabled: alert.email_enabled,
          slack_webhook_url: alert.slack_webhook_url || '',
          discord_webhook_url: alert.discord_webhook_url || ''
        })
      });
      await loadAlerts();
    } catch (err) {
      showMessage(t('agency.updateError'), 'error');
    }
  };

  const addDomainGroup = async () => {
    if (!newGroup.name) return;
    try {
      setSaving(true);
      const res = await fetch('/api/user/domain-groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newGroup)
      });
      if (res.ok) {
        await loadDomainGroups();
        setNewGroup({ name: '', description: '' });
        setShowAddGroup(false);
        showMessage(t('agency.groupAdded'));
      } else {
        const error = await res.json();
        showMessage(error.error || t('agency.updateError'), 'error');
      }
    } catch (err) {
      showMessage(t('agency.updateError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteDomainGroup = async (id) => {
    if (!confirm(t('common.confirmDelete'))) return;
    try {
      await fetch(`/api/user/domain-groups/${id}`, { method: 'DELETE', credentials: 'include' });
      await loadDomainGroups();
      showMessage(t('agency.groupDeleted'));
    } catch (err) {
      showMessage(t('agency.updateError'), 'error');
    }
  };

  const addMemberToGroup = async (groupId, originId) => {
    try {
      await fetch(`/api/user/domain-groups/${groupId}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ originId })
      });
      await loadDomainGroups();
    } catch (err) {
      showMessage(t('agency.updateError'), 'error');
    }
  };

  const removeMemberFromGroup = async (groupId, originId) => {
    try {
      await fetch(`/api/user/domain-groups/${groupId}/members/${originId}`, { method: 'DELETE', credentials: 'include' });
      await loadDomainGroups();
    } catch (err) {
      showMessage(t('agency.updateError'), 'error');
    }
  };

  const addClient = async () => {
    if (!newClient.clientName) return;
    
    try {
      setSaving(true);
      const res = await fetch('/api/agency/clients', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newClient)
      });
      
      if (res.ok) {
        await loadAgencyData();
        setNewClient({ clientName: '', clientEmail: '', originId: '' });
        setShowAddForm(false);
        showMessage(t('agency.clientAdded'));
      } else {
        const error = await res.json();
        showMessage(error.error || t('agency.updateError'), 'error');
      }
    } catch (err) {
      console.error('Failed to add client:', err);
      showMessage(t('agency.updateError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteClient = async (id) => {
    if (!confirm(t('common.confirmDelete'))) return;
    
    try {
      await fetch(`/api/agency/clients/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      await loadAgencyData();
      showMessage(t('agency.clientDeleted'));
    } catch (err) {
      console.error('Failed to delete client:', err);
      showMessage(t('agency.updateError'), 'error');
    }
  };

  const updateClientSettings = async (id, updates) => {
    try {
      const res = await fetch(`/api/agency/clients/${id}/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      });
      
      if (res.ok) {
        const data = await res.json();
        setClients(prev => prev.map(c => c.id === id ? { ...c, ...data.client } : c));
      } else {
        const error = await res.json();
        showMessage(error.error || t('agency.updateError'), 'error');
      }
    } catch (err) {
      console.error('Failed to update client settings:', err);
      showMessage(t('agency.updateError'), 'error');
    }
  };

  const generateClientToken = async (id) => {
    try {
      const res = await fetch(`/api/agency/clients/${id}/token`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setClients(prev => prev.map(c => c.id === id ? { ...c, access_token: data.token } : c));
        showMessage(t('agency.linkGenerated'));
      }
    } catch (err) {
      console.error('Failed to generate token:', err);
      showMessage(t('agency.updateError'), 'error');
    }
  };

  const sendClientReport = async (id) => {
    try {
      const res = await fetch(`/api/agency/clients/${id}/send-report`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (res.ok) {
        await loadAgencyData();
        showMessage(t('agency.reportSent'));
      } else {
        const error = await res.json();
        showMessage(error.error || t('agency.reportError'), 'error');
      }
    } catch (err) {
      console.error('Failed to send report:', err);
      showMessage(t('agency.reportError'), 'error');
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      await fetch('/api/agency/settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settings)
      });
      showMessage(t('common.settingsSaved'));
    } catch (err) {
      console.error('Failed to save settings:', err);
      showMessage(t('agency.updateError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const displayDomain = (origin) => {
    try {
      return new URL(origin).hostname.replace(/^www\./, '');
    } catch {
      return origin || t('common.notAssigned');
    }
  };

  const activeClients = clients.filter(c => c.access_token);
  const clientsWithScore = clients.filter(c => c.latest_score != null);
  const avgScore = clientsWithScore.length > 0 
    ? Math.round(clientsWithScore.reduce((sum, c) => sum + parseInt(c.latest_score), 0) / clientsWithScore.length)
    : null;

  if (loading) {
    return (
      <div className="cv-app-portal fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
      </div>
    );
  }

  if (loadError || !status?.isAgency) {
    return (
      <div className="cv-app-portal fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
            <Icons.Lock className="w-8 h-8 text-purple-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">{t('agency.agencyPortal')}</h2>
          <p role={loadError ? 'alert' : undefined} className="text-slate-600 dark:text-slate-400 mb-6">{loadError || t('common.proOnly')}</p>
          {loadError && <button onClick={loadAgencyData} className="mr-3 px-6 py-2.5 rounded-lg bg-purple-600 text-white">{language === 'cs' ? 'Zkusit znovu' : 'Retry'}</button>}
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-white text-sm font-medium transition-colors"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cv-app-portal cv-agency-dashboard dark fixed inset-0 z-50 overflow-hidden text-slate-100" style={{ background: '#0f172a' }}>
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500 rounded-full blur-3xl" />
      </div>
      
      {message && (
        <div className={`fixed top-4 right-4 z-[60] px-4 py-3 rounded-xl shadow-lg text-sm font-medium ${
          message.type === 'error' 
            ? 'bg-red-500 text-white' 
            : 'bg-green-500 text-white'
        }`}>
          {message.text}
        </div>
      )}
      
      <div className="relative z-10 h-full flex flex-col md:flex-row min-w-0">
        <div className="w-full md:w-64 md:shrink-0 border-r border-white/10 p-3 md:p-6 flex flex-col max-h-[38vh] md:max-h-none" style={{ background: '#172338' }}>
          <div className="flex items-center gap-3 mb-8">
            {settings.agencyLogoUrl ? (
              <img src={settings.agencyLogoUrl} alt="" className="w-10 h-10 rounded-xl object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white font-bold text-lg">
                {(settings.agencyName || 'A').charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <h2 className="font-bold text-white truncate">{settings.agencyName || t('agencyPreview.yourAgency')}</h2>
              <p className="text-xs text-blue-300/70">Performance Dashboard</p>
            </div>
          </div>
          
          <nav className="flex-1 space-y-1 overflow-y-auto">
            <button
              onClick={() => setActiveTab('clients')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'clients' 
                  ? 'bg-white/10 text-white' 
                  : 'text-blue-200/70 hover:text-white hover:bg-white/5'
              }`}
            >
              <Icons.Users className="w-5 h-5" />
              {t('common.clients')}
              <span className="ml-auto px-2 py-0.5 rounded-full bg-white/10 text-xs">{clients.length}</span>
            </button>
            
            <button
              onClick={() => setActiveTab('reports')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'reports' 
                  ? 'bg-white/10 text-white' 
                  : 'text-blue-200/70 hover:text-white hover:bg-white/5'
              }`}
            >
              <Icons.FileText className="w-5 h-5" />
              {t('agency.reports')}
            </button>
            
            <button
              onClick={() => { setActiveTab('alerts'); loadAlerts(); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'alerts' 
                  ? 'bg-white/10 text-white' 
                  : 'text-blue-200/70 hover:text-white hover:bg-white/5'
              }`}
            >
              <Icons.Bell className="w-5 h-5" />
              {t('agency.alerts')}
              {alerts.length > 0 && <span className="ml-auto px-2 py-0.5 rounded-full bg-white/10 text-xs">{alerts.length}</span>}
            </button>
            
            <button
              onClick={() => { setActiveTab('domains'); loadDomainGroups(); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'domains' 
                  ? 'bg-white/10 text-white' 
                  : 'text-blue-200/70 hover:text-white hover:bg-white/5'
              }`}
            >
              <Icons.Globe className="w-5 h-5" />
              {t('agency.domains')}
              {domainGroups.length > 0 && <span className="ml-auto px-2 py-0.5 rounded-full bg-white/10 text-xs">{domainGroups.length}</span>}
            </button>
            
            <button
              onClick={() => setActiveTab('script-monitoring')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'script-monitoring'
                  ? 'bg-white/10 text-white'
                  : 'text-blue-200/70 hover:text-white hover:bg-white/5'
              }`}
            >
              <Icons.Activity className="w-5 h-5" />
              {language === 'cs' ? 'Tagy & skripty' : 'Tags & scripts'}
              <span className="ml-auto text-[9px] px-1.5 py-0.5 bg-cyan-500/30 text-cyan-300 rounded font-bold">NEW</span>
            </button>

            <button
              onClick={() => setActiveTab('sitemap-monitoring')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'sitemap-monitoring'
                  ? 'bg-white/10 text-white'
                  : 'text-blue-200/70 hover:text-white hover:bg-white/5'
              }`}
            >
              <Icons.Map className="w-5 h-5" />
              {language === 'cs' ? 'Sitemapy & SEO' : 'Sitemaps & SEO'}
              <span className="ml-auto text-[9px] px-1.5 py-0.5 bg-cyan-500/30 text-cyan-300 rounded font-bold">NEW</span>
            </button>

            <button
              onClick={() => setActiveTab('settings')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'settings' 
                  ? 'bg-white/10 text-white' 
                  : 'text-blue-200/70 hover:text-white hover:bg-white/5'
              }`}
            >
              <Icons.Settings className="w-5 h-5" />
              {t('common.settings')}
            </button>
            
            <div className="pt-4 mt-4 border-t border-white/10">
              <div className="text-xs font-semibold text-blue-300/50 uppercase tracking-wider mb-2 px-4">{t('agency.analysisTools')}</div>
              
              <button
                onClick={() => setActiveTab('full-analysis')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  activeTab === 'full-analysis' 
                    ? 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-white border border-blue-500/30' 
                    : 'text-blue-200/70 hover:text-white hover:bg-white/5'
                }`}
              >
                <Icons.Activity className="w-5 h-5" />
                {language === 'cs' ? 'Kompletní analýza' : 'Full Analysis'}
                <span className="ml-auto text-[9px] px-1.5 py-0.5 bg-blue-500/30 text-blue-300 rounded font-bold">NEW</span>
              </button>

              <button
                onClick={() => setActiveTab('eshop-health')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  activeTab === 'eshop-health' 
                    ? 'bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-white border border-cyan-500/30' 
                    : 'text-blue-200/70 hover:text-white hover:bg-white/5'
                }`}
              >
                <Icons.Shield className="w-5 h-5" />
                {language === 'cs' ? 'E-shop Health Check' : 'E-shop Health Check'}
              </button>

              <button
                onClick={() => setActiveTab('health-check')}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  activeTab === 'health-check' 
                    ? 'bg-gradient-to-r from-emerald-500/20 to-blue-500/20 text-white border border-emerald-500/30' 
                    : 'text-blue-200/70 hover:text-white hover:bg-white/5'
                }`}
              >
                <Icons.Heart className="w-5 h-5" />
                Health Check
              </button>
              
              <button
                onClick={() => toggleMenu('labTests')}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-blue-200/70 hover:text-white hover:bg-white/5 transition-all"
              >
                <Icons.Beaker className="w-5 h-5" />
                {t('agency.labTests')}
                <Icons.ChevronDown className={`w-4 h-4 ml-auto transition-transform ${expandedMenus.labTests ? 'rotate-180' : ''}`} />
              </button>
              {expandedMenus.labTests && (
                <div className="ml-4 pl-4 border-l border-white/10 space-y-1 py-1">
                  {quickTools.map(tool => (
                    <button
                      key={tool.id}
                      onClick={() => setActiveTab(`lab-${tool.id}`)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                        activeTab === `lab-${tool.id}` 
                          ? tool.premium ? 'bg-purple-500/20 text-purple-300' : 'bg-white/10 text-white'
                          : tool.premium ? 'text-purple-300/60 hover:text-purple-300 hover:bg-purple-500/10' : 'text-blue-200/60 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <tool.icon className="w-3.5 h-3.5" />
                      {t(tool.labelKey)}
                      {tool.premium && <span className="ml-auto text-[9px] px-1 py-0.5 bg-purple-500/30 text-purple-300 rounded">PRO</span>}
                    </button>
                  ))}
                </div>
              )}
              
              <button
                onClick={() => toggleMenu('crux')}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-blue-200/70 hover:text-white hover:bg-white/5 transition-all"
              >
                <Icons.Activity className="w-5 h-5" />
                CrUX
                <Icons.ChevronDown className={`w-4 h-4 ml-auto transition-transform ${expandedMenus.crux ? 'rotate-180' : ''}`} />
              </button>
              {expandedMenus.crux && (
                <div className="ml-4 pl-4 border-l border-white/10 space-y-1 py-1">
                  {cruxMetrics.map(metric => (
                    <button
                      key={metric.id}
                      onClick={() => setActiveTab(`crux-${metric.id}`)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                        activeTab === `crux-${metric.id}` 
                          ? 'bg-white/10 text-white' 
                          : 'text-blue-200/60 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <Icons.BarChart className="w-3.5 h-3.5" />
                      {t(metric.labelKey)}
                    </button>
                  ))}
                </div>
              )}
              
              <button
                onClick={() => toggleMenu('pagespeed')}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-blue-200/70 hover:text-white hover:bg-white/5 transition-all"
              >
                <Icons.Gauge className="w-5 h-5" />
                PageSpeed
                <Icons.ChevronDown className={`w-4 h-4 ml-auto transition-transform ${expandedMenus.pagespeed ? 'rotate-180' : ''}`} />
              </button>
              {expandedMenus.pagespeed && (
                <div className="ml-4 pl-4 border-l border-white/10 space-y-1 py-1">
                  {pagespeedAudits.map(audit => (
                    <button
                      key={audit.id}
                      onClick={() => setActiveTab(`pagespeed-${audit.id}`)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                        activeTab === `pagespeed-${audit.id}` 
                          ? 'bg-white/10 text-white' 
                          : 'text-blue-200/60 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <Icons.Check className="w-3.5 h-3.5" />
                      {t(audit.labelKey)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </nav>
          
          <div className="pt-4 border-t border-white/10">
            <div className="text-xs text-blue-200/50 mb-2">{t('agency.usage')}</div>
            <div className="flex items-center gap-2 mb-2">
              <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full"
                  style={{ width: `${(clients.length / (status.maxClients || 50)) * 100}%` }}
                />
              </div>
              <span className="text-xs text-blue-200/70">{clients.length}/{status.maxClients || 50}</span>
            </div>
          </div>
          
          <button
            onClick={onClose}
            className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-blue-200/70 hover:text-white text-sm font-medium transition-all"
          >
            <Icons.X className="w-4 h-4" />
            {t('common.close')}
          </button>
        </div>
        
        <div className="cv-agency-content flex-1 min-h-0 min-w-0 overflow-y-auto" style={{ background: '#0f172a' }}>
          <div className="p-4 md:p-8 max-w-5xl mx-auto">
            {activeTab === 'clients' && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <StatCard icon={Icons.Users} value={clients.length} label={t('agency.totalClients')} color="cyan" />
                  <StatCard icon={Icons.CheckCircle} value={activeClients.length} label={t('agency.activeDashboards')} color="green" />
                  <StatCard icon={Icons.TrendingUp} value={avgScore != null ? `${avgScore}%` : '-'} label={t('agency.avgScore')} color="purple" />
                  <StatCard icon={Icons.Mail} value={clients.filter(c => c.report_frequency !== 'none').length} label={t('agency.autoReports')} color="orange" />
                </div>
                
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-white">{t('common.clients')}</h3>
                  <button
                    onClick={() => setShowAddForm(!showAddForm)}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white text-sm font-medium shadow-lg shadow-purple-500/25 transition-all"
                  >
                    <Icons.Plus className="w-4 h-4" />
                    {t('common.addClient')}
                  </button>
                </div>
                
                {showAddForm && (
                  <div className="mb-6 p-6 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-xl">
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-4">{t('agency.addNewClient')}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">{t('agency.clientName')} *</label>
                        <input
                          type="text"
                          value={newClient.clientName}
                          onChange={(e) => setNewClient({ ...newClient, clientName: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                          placeholder="Acme Corp"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">{t('agency.email')}</label>
                        <input
                          type="email"
                          value={newClient.clientEmail}
                          onChange={(e) => setNewClient({ ...newClient, clientEmail: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                          placeholder="client@example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">{t('common.domain')}</label>
                        <select
                          value={newClient.originId}
                          onChange={(e) => setNewClient({ ...newClient, originId: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                        >
                          <option value="">{t('common.assignDomain')}</option>
                          {origins.map(o => (
                            <option key={o.id} value={o.id}>{displayDomain(o.origin || o.url)}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={addClient}
                        disabled={!newClient.clientName || saving}
                        className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-pink-600 text-white text-sm font-medium disabled:opacity-50 transition-all"
                      >
                        {saving ? t('common.adding') : t('common.add')}
                      </button>
                      <button
                        onClick={() => setShowAddForm(false)}
                        className="px-6 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-white text-sm font-medium transition-colors"
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                )}
                
                {clients.length === 0 ? (
                  <div className="text-center py-16 bg-white/5 rounded-2xl backdrop-blur-sm border border-white/10">
                    <Icons.Users className="w-16 h-16 mx-auto mb-4 text-blue-300/30" />
                    <p className="text-blue-200/70 mb-2">{t('common.noClientsYet')}</p>
                    <p className="text-sm text-blue-200/50">{t('agency.addFirstClient')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {clients.map(client => (
                      <ClientCard
                        key={client.id}
                        client={client}
                        onDelete={deleteClient}
                        onUpdate={updateClientSettings}
                        onGenerateToken={generateClientToken}
                        onSendReport={sendClientReport}
                        displayDomain={displayDomain}
                        t={t}
                        language={language}
                        isExpanded={expandedClientId === client.id}
                        onToggle={() => setExpandedClientId(expandedClientId === client.id ? null : client.id)}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
            
            {activeTab === 'reports' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-white">{t('agency.reports')}</h3>
                    <p className="text-sm text-blue-200/70">{t('agency.reportsPageDesc')}</p>
                  </div>
                </div>
                
                {/* Scheduled Reports Info */}
                <div className="p-5 rounded-2xl bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <Icons.Clock className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">{t('agency.scheduledReports')}</h4>
                      <p className="text-sm text-blue-200/70 mb-3">{t('agency.scheduledReportsDesc')}</p>
                      <div className="flex gap-4 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Icons.Calendar className="w-3 h-3" />
                          {t('agency.weeklyReportsMonday')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Icons.Calendar className="w-3 h-3" />
                          {t('agency.monthlyReportsFirst')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Client Report Settings */}
                {clients.length === 0 ? (
                  <div className="text-center py-16 bg-white/5 rounded-2xl backdrop-blur-sm border border-white/10">
                    <Icons.Users className="w-16 h-16 mx-auto mb-4 text-blue-300/30" />
                    <p className="text-blue-200/70 mb-2">{t('agency.noClientsForReports')}</p>
                    <p className="text-sm text-blue-200/50">{t('agency.addClientsFirst')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-white">{t('agency.clientReportSettings')}</h4>
                    <div className="space-y-3">
                      {clients.map(client => (
                        <div key={client.id} className="p-4 rounded-xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white font-bold">
                                {client.client_name?.charAt(0)?.toUpperCase() || 'C'}
                              </div>
                              <div>
                                <div className="font-semibold text-slate-900 dark:text-white">{client.client_name}</div>
                                <div className="text-sm text-slate-500 dark:text-slate-400">{client.client_email}</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <div className="text-xs text-slate-500 dark:text-slate-400">{t('agency.reportFrequency')}</div>
                                <select
                                  value={client.report_frequency || 'weekly'}
                                  onChange={(e) => updateClientSettings(client.id, { reportFrequency: e.target.value })}
                                  className="mt-1 px-3 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-sm focus:border-purple-500 focus:outline-none"
                                >
                                  <option value="weekly">{t('agency.weekly')}</option>
                                  <option value="monthly">{t('agency.monthly')}</option>
                                  <option value="none">{t('agency.noReports')}</option>
                                </select>
                              </div>
                              <div className="text-right">
                                <div className="text-xs text-slate-500 dark:text-slate-400">{t('agency.lastReportSent')}</div>
                                <div className="text-sm text-slate-700 dark:text-slate-300">
                                  {client.last_report_sent_at 
                                    ? new Date(client.last_report_sent_at).toLocaleDateString(language === 'cs' ? 'cs-CZ' : 'en-US')
                                    : t('agency.never')
                                  }
                                </div>
                              </div>
                              <button
                                onClick={() => sendClientReport(client.id)}
                                className="px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white text-sm font-medium shadow-lg shadow-purple-500/25 transition-all"
                              >
                                {t('agency.sendNow')}
                              </button>
                            </div>
                          </div>
                          {client.origin && (
                            <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700/50 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                              <Icons.Globe className="w-4 h-4" />
                              {displayDomain(client.origin)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Report Content Info */}
                <div className="p-5 rounded-2xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                  <h4 className="font-semibold text-slate-900 dark:text-white mb-4">{t('agency.reportContents')}</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-900/50 text-center">
                      <Icons.Activity className="w-6 h-6 mx-auto mb-2 text-green-500" />
                      <div className="text-xs text-slate-500 dark:text-slate-400">{t('agency.coreWebVitals')}</div>
                    </div>
                    <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-900/50 text-center">
                      <Icons.TrendingUp className="w-6 h-6 mx-auto mb-2 text-blue-500" />
                      <div className="text-xs text-slate-500 dark:text-slate-400">{t('agency.performanceTrends')}</div>
                    </div>
                    <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-900/50 text-center">
                      <Icons.AlertTriangle className="w-6 h-6 mx-auto mb-2 text-amber-500" />
                      <div className="text-xs text-slate-500 dark:text-slate-400">{t('agency.issuesDetected')}</div>
                    </div>
                    <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-900/50 text-center">
                      <Icons.Target className="w-6 h-6 mx-auto mb-2 text-purple-500" />
                      <div className="text-xs text-slate-500 dark:text-slate-400">{t('agency.recommendations')}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'alerts' && (
              <>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-white">{t('agency.alerts')}</h3>
                    <p className="text-sm text-blue-200/70">{t('agency.alertsDescription')}</p>
                  </div>
                  {alertsFeatures.has_access && (
                    <button
                      onClick={() => setShowAddAlert(!showAddAlert)}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white text-sm font-medium shadow-lg shadow-purple-500/25 transition-all"
                    >
                      <Icons.Plus className="w-4 h-4" />
                      {t('agency.addAlert')}
                    </button>
                  )}
                </div>
                
                {!alertsFeatures.has_access ? (
                  <div className="text-center py-16 bg-white/5 rounded-2xl backdrop-blur-sm border border-white/10">
                    <Icons.Lock className="w-16 h-16 mx-auto mb-4 text-blue-300/30" />
                    <h3 className="text-xl font-bold text-white mb-2">{t('agency.alertsPremiumOnly')}</h3>
                    <p className="text-blue-200/70">{t('agency.alertsPremiumDesc')}</p>
                  </div>
                ) : alertsLoading ? (
                  <div className="text-center py-16">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent mx-auto"></div>
                  </div>
                ) : (
                  <>
                    {showAddAlert && (
                      <div className="mb-6 p-6 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-xl">
                        <h4 className="font-semibold text-slate-900 dark:text-white mb-4">{t('agency.addAlert')}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">{t('agency.alertDomain')}</label>
                            <select
                              value={newAlert.domain}
                              onChange={(e) => setNewAlert({ ...newAlert, domain: e.target.value })}
                              className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                            >
                              <option value="">{t('agency.allDomains')}</option>
                              {origins.map(o => (
                                <option key={o.id} value={o.origin}>{displayDomain(o.origin)}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">{t('agency.alertFrequency')}</label>
                            <select
                              value={newAlert.frequency}
                              onChange={(e) => setNewAlert({ ...newAlert, frequency: e.target.value })}
                              className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                            >
                              <option value="daily">{t('agency.daily')}</option>
                              <option value="weekly">{t('agency.frequencies.weekly')}</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">LCP {t('agency.changeThreshold')} (%)</label>
                            <input
                              type="number"
                              value={newAlert.alert_on_lcp_change}
                              onChange={(e) => setNewAlert({ ...newAlert, alert_on_lcp_change: parseInt(e.target.value) || 20 })}
                              className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                              min="1" max="100"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">INP {t('agency.changeThreshold')} (%)</label>
                            <input
                              type="number"
                              value={newAlert.alert_on_inp_change}
                              onChange={(e) => setNewAlert({ ...newAlert, alert_on_inp_change: parseInt(e.target.value) || 20 })}
                              className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                              min="1" max="100"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">CLS {t('agency.changeThreshold')} (%)</label>
                            <input
                              type="number"
                              value={newAlert.alert_on_cls_change}
                              onChange={(e) => setNewAlert({ ...newAlert, alert_on_cls_change: parseInt(e.target.value) || 50 })}
                              className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                              min="1" max="100"
                            />
                          </div>
                          {alertsFeatures.has_slack_webhook && (
                            <div>
                              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Slack Webhook</label>
                              <input
                                type="url"
                                value={newAlert.slackWebhook}
                                onChange={(e) => setNewAlert({ ...newAlert, slackWebhook: e.target.value })}
                                className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                                placeholder="https://hooks.slack.com/..."
                              />
                            </div>
                          )}
                          {alertsFeatures.has_discord_webhook && (
                            <div>
                              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Discord Webhook</label>
                              <input
                                type="url"
                                value={newAlert.discordWebhook}
                                onChange={(e) => setNewAlert({ ...newAlert, discordWebhook: e.target.value })}
                                className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                                placeholder="https://discord.com/api/webhooks/..."
                              />
                            </div>
                          )}
                        </div>
                        <div className="flex gap-3">
                          <button
                            onClick={addAlert}
                            disabled={saving}
                            className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white text-sm font-medium disabled:opacity-50 transition-all"
                          >
                            {saving ? t('common.saving') : t('agency.addAlert')}
                          </button>
                          <button
                            onClick={() => setShowAddAlert(false)}
                            className="px-6 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-white text-sm font-medium transition-colors"
                          >
                            {t('common.cancel')}
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {alerts.length === 0 ? (
                      <div className="text-center py-16 bg-white/5 rounded-2xl backdrop-blur-sm border border-white/10">
                        <Icons.Bell className="w-16 h-16 mx-auto mb-4 text-blue-300/30" />
                        <p className="text-blue-200/70 mb-2">{t('agency.noAlerts')}</p>
                        <p className="text-sm text-blue-200/50">{t('agency.noAlertsDesc')}</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {alerts.map(alert => (
                          <div key={alert.id} className="rounded-xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 overflow-hidden">
                            <div className="p-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                  <div className={`w-3 h-3 rounded-full ${alert.is_enabled ? 'bg-green-500' : 'bg-slate-400'}`} />
                                  <div>
                                    <div className="font-semibold text-slate-900 dark:text-white">
                                      {alert.domain ? displayDomain(alert.domain) : t('agency.allDomains')}
                                    </div>
                                    <div className="text-sm text-slate-500 dark:text-slate-400">
                                      LCP: {alert.alert_on_lcp_change}% • INP: {alert.alert_on_inp_change}% • CLS: {alert.alert_on_cls_change}%
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => toggleAlert(alert.id, alert)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                      alert.is_enabled 
                                        ? 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400' 
                                        : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                                    }`}
                                  >
                                    {alert.is_enabled ? t('common.active') : t('common.inactive')}
                                  </button>
                                  <button
                                    onClick={() => editingAlertId === alert.id ? cancelEditAlert() : startEditAlert(alert)}
                                    className="p-2 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-500/20 text-purple-500 transition-colors"
                                  >
                                    <Icons.Edit className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => deleteAlert(alert.id)}
                                    className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-500/20 text-red-500 transition-colors"
                                  >
                                    <Icons.Trash className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                            
                            {editingAlertId === alert.id && (
                              <div className="px-4 pb-4 border-t border-slate-100 dark:border-slate-700/50 pt-4 space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">{t('common.domain')}</label>
                                    <select
                                      value={newAlert.domain}
                                      onChange={(e) => setNewAlert({ ...newAlert, domain: e.target.value })}
                                      className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-sm focus:border-purple-500 focus:outline-none"
                                    >
                                      <option value="">{t('agency.allDomains')}</option>
                                      {origins.map(o => (
                                        <option key={o.id} value={o.origin}>{displayDomain(o.origin)}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">{t('agency.frequency')}</label>
                                    <select
                                      value={newAlert.frequency}
                                      onChange={(e) => setNewAlert({ ...newAlert, frequency: e.target.value })}
                                      className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-sm focus:border-purple-500 focus:outline-none"
                                    >
                                      <option value="daily">{t('agency.daily')}</option>
                                      <option value="weekly">{t('agency.weekly')}</option>
                                      <option value="monthly">{t('agency.monthly')}</option>
                                    </select>
                                  </div>
                                </div>
                                
                                <div className="grid grid-cols-3 gap-4">
                                  <div>
                                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">LCP {t('agency.threshold')} (%)</label>
                                    <input
                                      type="number"
                                      value={newAlert.alert_on_lcp_change}
                                      onChange={(e) => setNewAlert({ ...newAlert, alert_on_lcp_change: e.target.value })}
                                      className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-sm focus:border-purple-500 focus:outline-none"
                                      min="1"
                                      max="100"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">INP {t('agency.threshold')} (%)</label>
                                    <input
                                      type="number"
                                      value={newAlert.alert_on_inp_change}
                                      onChange={(e) => setNewAlert({ ...newAlert, alert_on_inp_change: e.target.value })}
                                      className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-sm focus:border-purple-500 focus:outline-none"
                                      min="1"
                                      max="100"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">CLS {t('agency.threshold')} (%)</label>
                                    <input
                                      type="number"
                                      value={newAlert.alert_on_cls_change}
                                      onChange={(e) => setNewAlert({ ...newAlert, alert_on_cls_change: e.target.value })}
                                      className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-sm focus:border-purple-500 focus:outline-none"
                                      min="1"
                                      max="100"
                                    />
                                  </div>
                                </div>
                                
                                {alertsFeatures.has_slack_webhook && (
                                  <div>
                                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Slack Webhook</label>
                                    <input
                                      type="url"
                                      value={newAlert.slackWebhook}
                                      onChange={(e) => setNewAlert({ ...newAlert, slackWebhook: e.target.value })}
                                      className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-sm focus:border-purple-500 focus:outline-none"
                                      placeholder="https://hooks.slack.com/services/..."
                                    />
                                  </div>
                                )}
                                
                                {alertsFeatures.has_discord_webhook && (
                                  <div>
                                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Discord Webhook</label>
                                    <input
                                      type="url"
                                      value={newAlert.discordWebhook}
                                      onChange={(e) => setNewAlert({ ...newAlert, discordWebhook: e.target.value })}
                                      className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-sm focus:border-purple-500 focus:outline-none"
                                      placeholder="https://discord.com/api/webhooks/..."
                                    />
                                  </div>
                                )}
                                
                                <div className="flex gap-3 pt-2">
                                  <button
                                    onClick={() => updateAlert(alert.id)}
                                    disabled={saving}
                                    className="px-5 py-2 rounded-xl bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white text-sm font-medium disabled:opacity-50 transition-all"
                                  >
                                    {saving ? t('common.saving') : t('common.save')}
                                  </button>
                                  <button
                                    onClick={cancelEditAlert}
                                    className="px-5 py-2 rounded-xl bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-white text-sm font-medium transition-colors"
                                  >
                                    {t('common.cancel')}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
            
            {activeTab === 'domains' && (
              <>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-white">{t('agency.domainGroups')}</h3>
                    <p className="text-sm text-blue-200/70">{t('agency.domainGroupsDesc')}</p>
                  </div>
                  {userTier === 'paid' && (
                    <button
                      onClick={() => setShowAddGroup(!showAddGroup)}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white text-sm font-medium shadow-lg shadow-purple-500/25 transition-all"
                    >
                      <Icons.Plus className="w-4 h-4" />
                      {t('agency.addGroup')}
                    </button>
                  )}
                </div>
                
                {domainsLoading || userTier === null ? (
                  <div className="text-center py-16">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent mx-auto"></div>
                  </div>
                ) : userTier === 'free' ? (
                  <div className="text-center py-16 bg-white/5 rounded-2xl backdrop-blur-sm border border-white/10">
                    <Icons.Lock className="w-16 h-16 mx-auto mb-4 text-blue-300/30" />
                    <h3 className="text-xl font-bold text-white mb-2">{t('agency.domainGroupsPremiumOnly')}</h3>
                    <p className="text-blue-200/70">{t('agency.domainGroupsPremiumDesc')}</p>
                  </div>
                ) : (
                  <>
                    {showAddGroup && (
                      <div className="mb-6 p-6 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-xl">
                        <h4 className="font-semibold text-slate-900 dark:text-white mb-4">{t('agency.addGroup')}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">{t('common.name')} *</label>
                            <input
                              type="text"
                              value={newGroup.name}
                              onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                              className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                              placeholder={t('agency.groupNamePlaceholder')}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">{t('common.description')}</label>
                            <input
                              type="text"
                              value={newGroup.description}
                              onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                              className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                              placeholder={t('agency.groupDescPlaceholder')}
                            />
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <button
                            onClick={addDomainGroup}
                            disabled={saving || !newGroup.name}
                            className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white text-sm font-medium disabled:opacity-50 transition-all"
                          >
                            {saving ? t('common.saving') : t('agency.addGroup')}
                          </button>
                          <button
                            onClick={() => setShowAddGroup(false)}
                            className="px-6 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-white text-sm font-medium transition-colors"
                          >
                            {t('common.cancel')}
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {domainGroups.length === 0 ? (
                      <div className="text-center py-16 bg-white/5 rounded-2xl backdrop-blur-sm border border-white/10">
                        <Icons.Folder className="w-16 h-16 mx-auto mb-4 text-blue-300/30" />
                        <p className="text-blue-200/70 mb-2">{t('agency.noGroups')}</p>
                        <p className="text-sm text-blue-200/50">{t('agency.noGroupsDesc')}</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {domainGroups.map(group => (
                          <div key={group.id} className="rounded-xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 overflow-hidden">
                            <div 
                              className="p-4 cursor-pointer flex items-center justify-between"
                              onClick={() => setExpandedGroupId(expandedGroupId === group.id ? null : group.id)}
                            >
                              <div className="flex items-center gap-4">
                                <Icons.Folder className="w-5 h-5 text-purple-500" />
                                <div>
                                  <div className="font-semibold text-slate-900 dark:text-white">{group.name}</div>
                                  {group.description && <div className="text-sm text-slate-500 dark:text-slate-400">{group.description}</div>}
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-700 text-xs text-slate-600 dark:text-slate-400">
                                  {group.members?.length || 0} {t('agency.domains')}
                                </span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); deleteDomainGroup(group.id); }}
                                  className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-500/20 text-red-500 transition-colors"
                                >
                                  <Icons.Trash className="w-4 h-4" />
                                </button>
                                <Icons.ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${expandedGroupId === group.id ? 'rotate-180' : ''}`} />
                              </div>
                            </div>
                            
                            {expandedGroupId === group.id && (
                              <div className="px-4 pb-4 border-t border-slate-100 dark:border-slate-700/50 pt-4">
                                <div className="mb-4">
                                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">{t('agency.addDomainToGroup')}</label>
                                  <select
                                    onChange={(e) => { if (e.target.value) addMemberToGroup(group.id, parseInt(e.target.value)); e.target.value = ''; }}
                                    className="w-full px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                                  >
                                    <option value="">{t('agency.selectOrigin')}</option>
                                    {origins.filter(o => !group.members?.some(m => m.origin_id === o.id)).map(o => (
                                      <option key={o.id} value={o.id}>{displayDomain(o.origin)}</option>
                                    ))}
                                  </select>
                                </div>
                                
                                {group.members?.length > 0 ? (
                                  <div className="space-y-2">
                                    {group.members.map(member => (
                                      <div key={member.origin_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-900/50">
                                        <span className="text-sm text-slate-700 dark:text-slate-300">{displayDomain(member.origin)}</span>
                                        <button
                                          onClick={() => removeMemberFromGroup(group.id, member.origin_id)}
                                          className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-500/20 text-red-500 transition-colors"
                                        >
                                          <Icons.X className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">{t('agency.noDomainsInGroup')}</p>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Unassigned Domains Section */}
                    {(() => {
                      const assignedOriginIds = new Set(
                        domainGroups.flatMap(g => (g.members || []).map(m => m.origin_id))
                      );
                      const unassigned = allDomains.filter(d => !assignedOriginIds.has(d.originId));
                      
                      return unassigned.length > 0 ? (
                        <div className="mt-8">
                          <div className="mb-4">
                            <h4 className="text-lg font-semibold text-slate-900 dark:text-white">{t('agency.unassignedDomains')}</h4>
                            <p className="text-sm text-slate-500 dark:text-slate-400">{t('agency.unassignedDomainsDesc')}</p>
                          </div>
                          <div className="space-y-2">
                            {unassigned.map(domain => (
                              <div key={domain.originId} className="flex items-center justify-between p-4 rounded-xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                                <div className="flex items-center gap-3">
                                  <Icons.Globe className="w-4 h-4 text-slate-400" />
                                  <span className="text-sm text-slate-700 dark:text-slate-300">{displayDomain(domain.origin)}</span>
                                </div>
                                {domainGroups.length > 0 && (
                                  <select
                                    onChange={(e) => { if (e.target.value) addMemberToGroup(parseInt(e.target.value), domain.originId); e.target.value = ''; }}
                                    className="px-3 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-xs focus:border-purple-500 focus:outline-none"
                                  >
                                    <option value="">{t('agency.assignToGroup')}</option>
                                    {domainGroups.map(g => (
                                      <option key={g.id} value={g.id}>{g.name}</option>
                                    ))}
                                  </select>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : allDomains.length > 0 && domainGroups.length > 0 ? (
                        <div className="mt-8 text-center py-8 bg-green-500/10 rounded-xl border border-green-500/20">
                          <Icons.Check className="w-8 h-8 mx-auto mb-2 text-green-500" />
                          <p className="text-green-400">{t('agency.noUnassignedDomains')}</p>
                        </div>
                      ) : null;
                    })()}
                  </>
                )}
              </>
            )}
            
            {activeTab === 'full-analysis' && (
              <Suspense fallback={
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
                </div>
              }>
                <AgencyFullAnalysis origins={origins} />
              </Suspense>
            )}

            {activeTab === 'eshop-health' && (
              <Suspense fallback={
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
                </div>
              }>
                <div className="max-w-4xl">
                  <EshopHealthCheckPage onPricingClick={() => {}} />
                </div>
              </Suspense>
            )}

            {activeTab === 'health-check' && (
              <Suspense fallback={
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
                </div>
              }>
                <div className="max-w-5xl">
                  <HealthCheckPage
                    navigateTo={() => {}}
                    currentUser={null}
                    onOpenAuth={() => {}}
                    onOpenPricing={() => {}}
                    embedded={true}
                  />
                </div>
              </Suspense>
            )}
            
            {activeTab.startsWith('lab-') && (
              <div className="max-w-4xl">
                {(() => {
                  const toolId = activeTab.replace('lab-', '');
                  const tool = quickTools.find(t => t.id === toolId);
                  const ToolIcon = tool?.icon || Icons.Beaker;
                  const isEmailTool = toolId === 'email-deliverability';
                  
                  return (
                    <div>
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-lg shadow-purple-500/25">
                          <ToolIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-white">{tool ? t(tool.labelKey) : toolId}</h3>
                          <p className="text-sm text-blue-200/70">{t('agency.runToolDesc')}</p>
                        </div>
                      </div>
                      
                      <form 
                        onSubmit={(e) => { e.preventDefault(); runToolAnalysis(toolId); }} 
                        className="mb-6 p-6 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
                      >
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          {isEmailTool ? t('quickTools.emailDomainCheck') : t('quickTools.enterUrl')}
                        </label>
                        <div className="flex gap-3">
                          {isEmailTool ? (
                            <input
                              type="text"
                              value={toolDomain}
                              onChange={(e) => setToolDomain(e.target.value)}
                              placeholder="example.com"
                              className="flex-1 px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                            />
                          ) : (
                            <input
                              type="text"
                              value={toolUrl}
                              onChange={(e) => setToolUrl(e.target.value)}
                              placeholder="https://example.com"
                              className="flex-1 px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                            />
                          )}
                          <button
                            type="submit"
                            disabled={toolLoading || (isEmailTool ? !toolDomain.trim() : !toolUrl.trim())}
                            className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white font-medium disabled:opacity-50 transition-all flex items-center gap-2"
                          >
                            {toolLoading ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                {t('common.analyzing')}
                              </>
                            ) : (
                              <>
                                <Icons.Play className="w-4 h-4" />
                                {t('common.analyze')}
                              </>
                            )}
                          </button>
                        </div>
                      </form>
                      
                      {toolError && (
                        <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 flex items-center gap-3">
                          <Icons.AlertTriangle className="w-5 h-5 flex-shrink-0" />
                          <span>{toolError}</span>
                          <button onClick={clearToolResult} className="ml-auto text-red-300 hover:text-white">
                            <Icons.X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      
                      {toolResult && toolResult.module === toolModuleMap[toolId] && (
                        <div className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-hidden">
                          <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Icons.CheckCircle className="w-5 h-5 text-green-500" />
                              <span className="font-medium text-slate-900 dark:text-white">{t('agency.analysisComplete')}</span>
                              <span className="text-sm text-slate-500">{toolResult.url}</span>
                            </div>
                            <button onClick={clearToolResult} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400">
                              <Icons.X className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="p-6 max-h-[60vh] overflow-y-auto">
                            <ToolResultDisplay result={toolResult} t={t} />
                          </div>
                        </div>
                      )}
                      
                      {!toolResult && !toolError && !toolLoading && (
                        <div className="text-center py-12 rounded-2xl bg-white/5 border border-white/10">
                          <ToolIcon className="w-12 h-12 mx-auto mb-4 text-blue-300/30" />
                          <p className="text-blue-200/50">{t('agency.enterUrlToStart')}</p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
            
            {activeTab.startsWith('crux-') && (
              <div className="max-w-4xl">
                {(() => {
                  const metricId = activeTab.replace('crux-', '');
                  const metric = cruxMetrics.find(m => m.id === metricId);
                  const metricLabels = { lcp: 'LCP', inp: 'INP', cls: 'CLS', fcp: 'FCP', ttfb: 'TTFB' };
                  const metricUnits = { lcp: 'ms', inp: 'ms', cls: '', fcp: 'ms', ttfb: 'ms' };
                  
                  const getMetricValue = (data, metric) => {
                    const metrics = data?.metrics || data?.raw?.record?.metrics || data?.record?.metrics;
                    if (!metrics) return null;
                    const metricKey = metric.toUpperCase() === 'CLS' ? 'cls' : 
                                      metric.toUpperCase() === 'LCP' ? 'lcp' :
                                      metric.toUpperCase() === 'INP' ? 'inp' :
                                      metric.toUpperCase() === 'FCP' ? 'fcp' :
                                      metric.toUpperCase() === 'TTFB' ? 'ttfb' : metric;
                    const metricData = metrics[metricKey];
                    if (metricData) return metricData;
                    const rawMetricKey = metric.toUpperCase() === 'CLS' ? 'cumulative_layout_shift' : 
                                         metric.toUpperCase() === 'LCP' ? 'largest_contentful_paint' :
                                         metric.toUpperCase() === 'INP' ? 'interaction_to_next_paint' :
                                         metric.toUpperCase() === 'FCP' ? 'first_contentful_paint' :
                                         metric.toUpperCase() === 'TTFB' ? 'experimental_time_to_first_byte' : metric;
                    const rawMetrics = data?.raw?.record?.metrics;
                    return rawMetrics?.[rawMetricKey] || null;
                  };
                  
                  return (
                    <div>
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/25">
                          <Icons.Activity className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-white">{metric ? t(metric.labelKey) : metricLabels[metricId] || metricId.toUpperCase()}</h3>
                          <p className="text-sm text-blue-200/70">{t('agency.cruxAnalysisDesc')}</p>
                        </div>
                      </div>
                      
                      <form 
                        onSubmit={(e) => { e.preventDefault(); runCruxAnalysis(metricId); }} 
                        className="mb-6 p-6 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{t('common.origin')}</label>
                            <input
                              type="text"
                              value={cruxUrl}
                              onChange={(e) => setCruxUrl(e.target.value)}
                              placeholder="https://example.com"
                              className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 focus:outline-none transition-all"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{t('common.device')}</label>
                            <select
                              value={cruxFormFactor}
                              onChange={(e) => setCruxFormFactor(e.target.value)}
                              className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 focus:outline-none transition-all"
                            >
                              <option value="PHONE">{t('common.mobile')}</option>
                              <option value="DESKTOP">{t('common.desktop')}</option>
                              <option value="ALL">{t('common.all')}</option>
                            </select>
                          </div>
                        </div>
                        <button
                          type="submit"
                          disabled={cruxLoading || !cruxUrl.trim()}
                          className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-medium disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                        >
                          {cruxLoading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              {t('common.analyzing')}
                            </>
                          ) : (
                            <>
                              <Icons.Play className="w-4 h-4" />
                              {t('agency.getCruxData')}
                            </>
                          )}
                        </button>
                      </form>
                      
                      {cruxError && (
                        <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 flex items-center gap-3">
                          <Icons.AlertTriangle className="w-5 h-5 flex-shrink-0" />
                          <span>{cruxError}</span>
                          <button onClick={clearCruxResult} className="ml-auto text-red-300 hover:text-white">
                            <Icons.X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      
                      {cruxResult && (
                        <div className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-hidden">
                          <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Icons.CheckCircle className="w-5 h-5 text-green-500" />
                              <span className="font-medium text-slate-900 dark:text-white">{t('agency.analysisComplete')}</span>
                              <span className="text-sm text-slate-500">{cruxResult.url} ({cruxResult.formFactor})</span>
                            </div>
                            <button onClick={clearCruxResult} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400">
                              <Icons.X className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="p-6">
                            {(() => {
                              const metricData = getMetricValue(cruxResult.data, metricId);
                              if (!metricData) {
                                return <p className="text-center text-slate-500">{t('agency.noDataForMetric')}</p>;
                              }
                              const p75 = metricData.percentiles?.p75;
                              const histogram = metricData.histogram;
                              return (
                                <div className="space-y-6">
                                  <div className="text-center">
                                    <div className="text-5xl font-bold text-cyan-400">{p75}{metricUnits[metricId]}</div>
                                    <div className="text-slate-500 mt-2">75th Percentile (p75)</div>
                                  </div>
                                  {histogram && (
                                    <div className="space-y-3">
                                      <div className="flex items-center gap-3">
                                        <div className="w-20 text-right text-sm text-slate-500">{t('common.good')}</div>
                                        <div className="flex-1 h-8 bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden">
                                          <div className="h-full bg-green-500" style={{width: `${(histogram[0]?.density || 0) * 100}%`}} />
                                        </div>
                                        <div className="w-16 text-sm text-slate-600 dark:text-slate-400">{((histogram[0]?.density || 0) * 100).toFixed(1)}%</div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <div className="w-20 text-right text-sm text-slate-500">{t('common.needsImprovement')}</div>
                                        <div className="flex-1 h-8 bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden">
                                          <div className="h-full bg-yellow-500" style={{width: `${(histogram[1]?.density || 0) * 100}%`}} />
                                        </div>
                                        <div className="w-16 text-sm text-slate-600 dark:text-slate-400">{((histogram[1]?.density || 0) * 100).toFixed(1)}%</div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <div className="w-20 text-right text-sm text-slate-500">{t('common.poor')}</div>
                                        <div className="flex-1 h-8 bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden">
                                          <div className="h-full bg-red-500" style={{width: `${(histogram[2]?.density || 0) * 100}%`}} />
                                        </div>
                                        <div className="w-16 text-sm text-slate-600 dark:text-slate-400">{((histogram[2]?.density || 0) * 100).toFixed(1)}%</div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                      
                      {!cruxResult && !cruxError && !cruxLoading && (
                        <div className="text-center py-12 rounded-2xl bg-white/5 border border-white/10">
                          <Icons.Activity className="w-12 h-12 mx-auto mb-4 text-blue-300/30" />
                          <p className="text-blue-200/50">{t('agency.enterOriginToStart')}</p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
            
            {activeTab.startsWith('pagespeed-') && (
              <div className="max-w-4xl">
                {(() => {
                  const auditId = activeTab.replace('pagespeed-', '');
                  const audit = pagespeedAudits.find(a => a.id === auditId);
                  const auditIcons = { 
                    performance: Icons.Gauge, 
                    accessibility: Icons.Eye, 
                    'best-practices': Icons.CheckCircle, 
                    seo: Icons.Search 
                  };
                  const AuditIcon = auditIcons[auditId] || Icons.Gauge;
                  
                  return (
                    <div>
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/25">
                          <AuditIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-white">{audit ? t(audit.labelKey) : auditId}</h3>
                          <p className="text-sm text-blue-200/70">{t('agency.lighthouseAuditDesc')}</p>
                        </div>
                      </div>
                      
                      <form 
                        onSubmit={(e) => { e.preventDefault(); runLighthouseAudit(auditId); }} 
                        className="mb-6 p-6 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{t('quickTools.enterUrl')}</label>
                            <input
                              type="text"
                              value={lighthouseUrl}
                              onChange={(e) => setLighthouseUrl(e.target.value)}
                              placeholder="https://example.com"
                              className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none transition-all"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{t('common.device')}</label>
                            <select
                              value={lighthouseFormFactor}
                              onChange={(e) => setLighthouseFormFactor(e.target.value)}
                              className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none transition-all"
                            >
                              <option value="mobile">{t('common.mobile')}</option>
                              <option value="desktop">{t('common.desktop')}</option>
                            </select>
                          </div>
                        </div>
                        <button
                          type="submit"
                          disabled={lighthouseLoading || !lighthouseUrl.trim()}
                          className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-medium disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                        >
                          {lighthouseLoading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              {t('agency.runningAudit')}
                            </>
                          ) : (
                            <>
                              <Icons.Play className="w-4 h-4" />
                              {t('agency.runAudit')}
                            </>
                          )}
                        </button>
                        <p className="text-xs text-slate-500 mt-3 text-center">{t('agency.lighthouseNote')}</p>
                      </form>
                      
                      {lighthouseError && (
                        <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 flex items-center gap-3">
                          <Icons.AlertTriangle className="w-5 h-5 flex-shrink-0" />
                          <span>{lighthouseError}</span>
                          <button onClick={clearLighthouseResult} className="ml-auto text-red-300 hover:text-white">
                            <Icons.X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      
                      {lighthouseResult && lighthouseResult.auditType === auditId && (
                        <div className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-hidden">
                          <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Icons.CheckCircle className="w-5 h-5 text-green-500" />
                              <span className="font-medium text-slate-900 dark:text-white">{t('agency.auditComplete')}</span>
                              <span className="text-sm text-slate-500">{lighthouseResult.url} ({lighthouseResult.formFactor})</span>
                            </div>
                            <button onClick={clearLighthouseResult} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400">
                              <Icons.X className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="p-6">
                            {(() => {
                              const result = lighthouseResult.data;
                              const categoryMap = {
                                'performance': 'performance',
                                'accessibility': 'accessibility', 
                                'best-practices': 'bestPractices',
                                'seo': 'seo'
                              };
                              const categoryKey = categoryMap[auditId] || auditId;
                              const categoryScore = result?.metrics?.[categoryKey]?.score;
                              const score = categoryScore != null ? Math.round(categoryScore * 100) : (result?.metrics?.performance?.score ? Math.round(result.metrics.performance.score * 100) : 0);
                              const scoreColor = score >= 90 ? 'text-green-500' : score >= 50 ? 'text-yellow-500' : 'text-red-500';
                              
                              const opportunities = result?.raw?.lighthouseResult?.audits ? 
                                Object.values(result.raw.lighthouseResult.audits)
                                  .filter(a => a.score !== null && a.score < 1 && a.details?.type === 'opportunity')
                                  .sort((a, b) => (b.details?.overallSavingsMs || 0) - (a.details?.overallSavingsMs || 0))
                                  .slice(0, 8) : [];
                              
                              return (
                                <div className="space-y-6">
                                  <div className="text-center">
                                    <div className={`text-6xl font-bold ${scoreColor}`}>{score}</div>
                                    <div className="text-slate-500 mt-2">{audit ? t(audit.labelKey) : auditId} Score</div>
                                  </div>
                                  {opportunities.length > 0 && (
                                    <div className="space-y-3">
                                      <h4 className="font-semibold text-slate-900 dark:text-white">{t('common.issues')}</h4>
                                      {opportunities.map((opp, i) => (
                                        <div key={i} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-900/50 text-sm">
                                          <div className="font-medium text-slate-900 dark:text-white">{opp.title}</div>
                                          {opp.displayValue && <div className="text-orange-500 mt-1">{opp.displayValue}</div>}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                      
                      {!lighthouseResult && !lighthouseError && !lighthouseLoading && (
                        <div className="text-center py-12 rounded-2xl bg-white/5 border border-white/10">
                          <AuditIcon className="w-12 h-12 mx-auto mb-4 text-blue-300/30" />
                          <p className="text-blue-200/50">{t('agency.enterUrlToStart')}</p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
            
            {activeTab === 'script-monitoring' && (
              <Suspense fallback={<div className="text-gray-400">Loading…</div>}>
                <ScriptMonitoringDashboardTab />
              </Suspense>
            )}

            {activeTab === 'sitemap-monitoring' && (
              <Suspense fallback={<div className="text-gray-400">Loading…</div>}>
                <SitemapMonitoringDashboardTab />
              </Suspense>
            )}

            {activeTab === 'settings' && (
              <div className="max-w-2xl space-y-6">
                <h3 className="text-xl font-bold text-white mb-6">{t('common.agencySettings')}</h3>
                
                {/* Basic Info */}
                <div className="p-6 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-xl space-y-6">
                  <div className="flex items-center gap-3 pb-4 border-b border-slate-200 dark:border-slate-700">
                    <Icons.Building className="w-5 h-5 text-purple-500" />
                    <h4 className="font-semibold text-slate-900 dark:text-white">{t('agency.basicInfo')}</h4>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{t('common.agencyName')}</label>
                    <input
                      type="text"
                      value={settings.agencyName}
                      onChange={(e) => setSettings({ ...settings, agencyName: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                      placeholder={t('common.yourAgency')}
                    />
                    <p className="mt-2 text-xs text-slate-500">{t('agency.agencyNameHint')}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{t('common.logoUrl')}</label>
                    <input
                      type="url"
                      value={settings.agencyLogoUrl}
                      onChange={(e) => setSettings({ ...settings, agencyLogoUrl: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                      placeholder="https://..."
                    />
                    <p className="mt-2 text-xs text-slate-500">{t('agency.logoUrlHint')}</p>
                    <div className="mt-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20">
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        <strong>{t('agency.logoRecommendations')}:</strong><br/>
                        • {t('agency.logoSize')}: 200x50px - 400x100px<br/>
                        • {t('agency.logoFormats')}: PNG, SVG, WebP<br/>
                        • {t('agency.logoTransparent')}
                      </p>
                    </div>
                  </div>
                  
                  {settings.agencyLogoUrl && (
                    <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
                      <p className="text-xs text-slate-500 mb-3">{t('agency.logoPreview')}</p>
                      <div className="flex gap-4">
                        <div className="p-4 bg-white rounded-lg">
                          <img 
                            src={settings.agencyLogoUrl} 
                            alt="Logo preview (light)" 
                            className="h-10 object-contain"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                        <div className="p-4 bg-slate-900 rounded-lg">
                          <img 
                            src={settings.agencyLogoUrl} 
                            alt="Logo preview (dark)" 
                            className="h-10 object-contain"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Brand Colors */}
                <div className="p-6 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-xl space-y-6">
                  <div className="flex items-center gap-3 pb-4 border-b border-slate-200 dark:border-slate-700">
                    <Icons.Palette className="w-5 h-5 text-pink-500" />
                    <h4 className="font-semibold text-slate-900 dark:text-white">{t('agency.brandColors')}</h4>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{t('agency.primaryColor')}</label>
                      <div className="flex items-center gap-3 min-w-0">
                        <input
                          type="color"
                          value={settings.primaryColor}
                          onChange={(e) => setSettings({ ...settings, primaryColor: e.target.value })}
                          className="w-12 h-12 shrink-0 rounded-lg border border-slate-200 dark:border-slate-600 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={settings.primaryColor}
                          onChange={(e) => setSettings({ ...settings, primaryColor: e.target.value })}
                          className="flex-1 min-w-0 px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-sm font-mono focus:border-purple-500 focus:outline-none"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{t('agency.secondaryColor')}</label>
                      <div className="flex items-center gap-3 min-w-0">
                        <input
                          type="color"
                          value={settings.secondaryColor}
                          onChange={(e) => setSettings({ ...settings, secondaryColor: e.target.value })}
                          className="w-12 h-12 shrink-0 rounded-lg border border-slate-200 dark:border-slate-600 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={settings.secondaryColor}
                          onChange={(e) => setSettings({ ...settings, secondaryColor: e.target.value })}
                          className="flex-1 min-w-0 px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white text-sm font-mono focus:border-purple-500 focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">{t('agency.brandColorsHint')}</p>
                </div>
                
                {/* Contact Info */}
                <div className="p-6 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-xl space-y-6">
                  <div className="flex items-center gap-3 pb-4 border-b border-slate-200 dark:border-slate-700">
                    <Icons.Mail className="w-5 h-5 text-cyan-500" />
                    <h4 className="font-semibold text-slate-900 dark:text-white">{t('agency.contactInfo')}</h4>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{t('agency.contactEmail')}</label>
                      <input
                        type="email"
                        value={settings.contactEmail}
                        onChange={(e) => setSettings({ ...settings, contactEmail: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white focus:border-purple-500 focus:outline-none transition-all"
                        placeholder="agency@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{t('agency.contactPhone')}</label>
                      <input
                        type="tel"
                        value={settings.contactPhone}
                        onChange={(e) => setSettings({ ...settings, contactPhone: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white focus:border-purple-500 focus:outline-none transition-all"
                        placeholder="+420 123 456 789"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{t('agency.website')}</label>
                    <input
                      type="url"
                      value={settings.website}
                      onChange={(e) => setSettings({ ...settings, website: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white focus:border-purple-500 focus:outline-none transition-all"
                      placeholder="https://www.your-agency.com"
                    />
                  </div>
                  <p className="text-xs text-slate-500">{t('agency.contactInfoHint')}</p>
                </div>
                
                {/* Report Settings */}
                <div className="p-6 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-xl space-y-6">
                  <div className="flex items-center gap-3 pb-4 border-b border-slate-200 dark:border-slate-700">
                    <Icons.FileText className="w-5 h-5 text-orange-500" />
                    <h4 className="font-semibold text-slate-900 dark:text-white">{t('agency.reportSettings')}</h4>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{t('agency.defaultReportLanguage')}</label>
                    <select
                      value={settings.defaultReportLanguage}
                      onChange={(e) => setSettings({ ...settings, defaultReportLanguage: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white focus:border-purple-500 focus:outline-none transition-all"
                    >
                      <option value="en">English</option>
                      <option value="cs">Čeština</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{t('agency.reportFooter')}</label>
                    <textarea
                      value={settings.reportFooter}
                      onChange={(e) => setSettings({ ...settings, reportFooter: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white focus:border-purple-500 focus:outline-none transition-all resize-none"
                      placeholder={t('agency.reportFooterPlaceholder')}
                    />
                    <p className="mt-2 text-xs text-slate-500">{t('agency.reportFooterHint')}</p>
                  </div>
                </div>
                
                {/* Save Button */}
                <button
                  onClick={saveSettings}
                  disabled={saving}
                  className="w-full px-6 py-4 rounded-xl bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white font-medium disabled:opacity-50 transition-all shadow-lg shadow-purple-500/25"
                >
                  {saving ? t('common.saving') : t('common.saveSettings')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgencyPanel;
